import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Info, GripVertical } from 'lucide-react';
import { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TemplateVariablesPanelProps {
  onInsertVariable: (variable: string) => void;
}

interface Variable {
  name: string;
  variable: string;
  description: string;
  category: string;
}

const TEMPLATE_VARIABLES: Variable[] = [
  // Customer Information
  {
    name: 'Customer Name',
    variable: '{{customer_name}}',
    description: 'Full name of the customer',
    category: 'Customer Information',
  },
  {
    name: 'Customer Email',
    variable: '{{customer_email}}',
    description: 'Email address of the customer',
    category: 'Customer Information',
  },
  {
    name: 'Customer Phone',
    variable: '{{customer_phone}}',
    description: 'Phone number of the customer',
    category: 'Customer Information',
  },
  {
    name: 'Account Number',
    variable: '{{account_number}}',
    description: 'Customer account number',
    category: 'Customer Information',
  },
  {
    name: 'Contact First Name',
    variable: '{{contact_first_name}}',
    description: 'First name of the primary contact person',
    category: 'Customer Information',
  },
  {
    name: 'Contact Surname',
    variable: '{{contact_surname}}',
    description: 'Surname of the primary contact person',
    category: 'Customer Information',
  },
  {
    name: 'Contact Email',
    variable: '{{contact_email}}',
    description: 'Email address of the primary contact',
    category: 'Customer Information',
  },
  {
    name: 'Contact Phone',
    variable: '{{contact_phone}}',
    description: 'Phone number of the primary contact',
    category: 'Customer Information',
  },
  {
    name: 'Account Balance',
    variable: '{{account_balance}}',
    description: 'Total account balance',
    category: 'Customer Information',
  },
  {
    name: 'Overdue Balance',
    variable: '{{overdue_balance}}',
    description: 'Total overdue balance amount',
    category: 'Customer Information',
  },
  {
    name: 'Credit Limit',
    variable: '{{credit_limit}}',
    description: 'Customer credit limit',
    category: 'Customer Information',
  },
  
  // Transaction Details
  {
    name: 'Reference Number',
    variable: '{{reference_number}}',
    description: 'Unique transaction reference identifier',
    category: 'Transaction Details',
  },
  {
    name: 'Transaction Date',
    variable: '{{transaction_date}}',
    description: 'Date the transaction was issued',
    category: 'Transaction Details',
  },
  {
    name: 'Due Date',
    variable: '{{due_date}}',
    description: 'Payment due date',
    category: 'Transaction Details',
  },
  {
    name: 'Gross',
    variable: '{{gross}}',
    description: 'Total gross amount',
    category: 'Transaction Details',
  },
  {
    name: 'Amount Due',
    variable: '{{amount_due}}',
    description: 'Outstanding amount to be paid',
    category: 'Transaction Details',
  },
  {
    name: 'Days Overdue',
    variable: '{{days_overdue}}',
    description: 'Number of days past due date',
    category: 'Transaction Details',
  },
  {
    name: 'Last Payment Date',
    variable: '{{last_payment_date}}',
    description: 'Date of the most recent payment',
    category: 'Transaction Details',
  },
  {
    name: 'Case Amount',
    variable: '{{case_amount}}',
    description: 'Total case amount',
    category: 'Transaction Details',
  },
  {
    name: 'Case Transactions',
    variable: '{{case_transactions}}',
    description: 'Table of case transactions with Reference, Date, Due Date, Gross Value, and Outstanding Amount',
    category: 'Transaction Details',
  },
  {
    name: 'Overdue Transactions',
    variable: '{{overdue_transactions}}',
    description: 'Table of overdue transactions with Reference, Date, Due Date, Gross Value, and Outstanding Amount',
    category: 'Transaction Details',
  },
  {
    name: 'Outstanding Transactions',
    variable: '{{outstanding_transactions}}',
    description: 'Table of outstanding transactions with Reference, Date, Due Date, Gross Value, and Outstanding Amount',
    category: 'Transaction Details',
  },
  
  // Company Information
  {
    name: 'Company Name',
    variable: '{{company_name}}',
    description: 'Your company name',
    category: 'Company Information',
  },
  {
    name: 'Company Email',
    variable: '{{company_email}}',
    description: 'Your company email address',
    category: 'Company Information',
  },
  {
    name: 'Company Phone',
    variable: '{{company_phone}}',
    description: 'Your company phone number',
    category: 'Company Information',
  },
  {
    name: 'Support Email',
    variable: '{{support_email}}',
    description: 'Customer support email',
    category: 'Company Information',
  },
  {
    name: 'Credit Controller First Name',
    variable: '{{credit_controller_first_name}}',
    description: 'First name of the assigned credit controller',
    category: 'Company Information',
  },
  {
    name: 'Credit Controller Last Name',
    variable: '{{credit_controller_last_name}}',
    description: 'Last name of the assigned credit controller',
    category: 'Company Information',
  },
  {
    name: 'Credit Controller Email',
    variable: '{{credit_controller_email}}',
    description: 'Email address of the credit controller',
    category: 'Company Information',
  },
  
  // Payment Information
  {
    name: 'Payment Link',
    variable: '{{payment_link}}',
    description: 'Link to payment portal',
    category: 'Payment Information',
  },
  {
    name: 'Payment Methods',
    variable: '{{payment_methods}}',
    description: 'Available payment methods',
    category: 'Payment Information',
  },
];

// Category color mapping
const getCategoryColor = (category: string): { 
  variant: 'default' | 'secondary' | 'outline' | 'accent', 
  accent?: 'destructive' | 'success' | 'warning' | 'info' | 'teal' | 'purple' | 'nile' | 'cerulean' | 'lagoon' | 'eagle' | 'sap' | 'orange' | 'brick' | 'red' | 'violet' | 'blue-violet'
} => {
  const colorMap: Record<string, { 
    variant: 'default' | 'secondary' | 'outline' | 'accent', 
    accent?: 'destructive' | 'success' | 'warning' | 'info' | 'teal' | 'purple' | 'nile' | 'cerulean' | 'lagoon' | 'eagle' | 'sap' | 'orange' | 'brick' | 'red' | 'violet' | 'blue-violet'
  }> = {
    'Customer Information': { variant: 'accent', accent: 'blue-violet' },
    'Transaction Details': { variant: 'accent', accent: 'teal' },
    'Company Information': { variant: 'accent', accent: 'purple' },
    'Payment Information': { variant: 'accent', accent: 'orange' },
  };
  return colorMap[category] || { variant: 'secondary' };
};

export function TemplateVariablesPanel({ onInsertVariable }: TemplateVariablesPanelProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isDragging, setIsDragging] = useState(false);

  // Group variables by category
  const categories = ['all', ...Array.from(new Set(TEMPLATE_VARIABLES.map(v => v.category)))];
  
  const filteredVariables = selectedCategory === 'all' 
    ? TEMPLATE_VARIABLES 
    : TEMPLATE_VARIABLES.filter(v => v.category === selectedCategory);

  const groupedVariables = filteredVariables.reduce((acc, variable) => {
    if (!acc[variable.category]) {
      acc[variable.category] = [];
    }
    acc[variable.category].push(variable);
    return acc;
  }, {} as Record<string, Variable[]>);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Click or drag to insert at cursor position
          </p>
        </div>

        {/* Category Filter */}
        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Filter by:</span>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category === 'all' ? 'All Variables' : category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Variables by Category */}
        <div className="space-y-4">
          {Object.entries(groupedVariables).map(([category, variables]) => (
            <div key={category} className="space-y-1.5">
              <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                {category}
              </h3>
              <div className="space-y-1">
                {variables.map((variable) => {
                  const categoryStyle = getCategoryColor(variable.category);
                  return (
                    <Tooltip key={variable.variable}>
                      <TooltipTrigger asChild>
                        <button
                          draggable
                          onDragStart={(e) => {
                            setIsDragging(true);
                            e.dataTransfer.effectAllowed = 'copy';
                            // Set both data types - the custom handler will prevent default insertion
                            e.dataTransfer.setData('text/plain', variable.variable);
                            e.dataTransfer.setData('application/x-template-variable', variable.variable);
                            // Add visual feedback
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            // Reset visual feedback
                            e.currentTarget.style.opacity = '1';
                            // Reset dragging state after a short delay to prevent click from firing
                            setTimeout(() => setIsDragging(false), 100);
                          }}
                          onClick={(e) => {
                            // Only insert on click if not dragging
                            if (!isDragging) {
                              onInsertVariable(variable.variable);
                            }
                          }}
                          className="group relative flex items-start gap-2 px-3 py-2.5 w-full border rounded-md hover:border-primary hover:bg-accent transition-colors text-left cursor-grab active:cursor-grabbing"
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground/50 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <span className="font-medium text-sm leading-tight block">
                              {variable.name}
                            </span>
                            <Badge 
                              variant={categoryStyle.variant}
                              accent={categoryStyle.accent}
                              className="text-[10px] font-mono"
                            >
                              {variable.variable}
                            </Badge>
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[250px]">
                        <p className="text-xs">{variable.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
              How to use
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-snug">
              Click your desired insertion point in the template, then click a variable to insert it. Or drag and drop a variable directly into the template. Hover over any variable to see its description.
            </p>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
